import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, EmptyState, IconButton, Page, PageHeader, Segmented, TableSkeleton } from "../../../components/ui";
import { getLiveSessions } from "../../../services/liveSessions";
import "../payments-ledger/payments.css";

const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const when = (date) => (date ? new Date(date).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const duration = (minutes, t) => {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return t("{{count}} min", { count: minutes });
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")}`;
};

/**
 * Lives of the app (Firestore): who hosted, when, how long, viewers now or at
 * the peak, participants; link to share. Lives in progress first.
 */
const Lives = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [env, setEnv] = useState("prod");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setData(null);
    setError("");
    getLiveSessions({ days, env })
      .then(setData)
      .catch((failure) => setError(failure?.response?.data?.message || "The lives could not be loaded."));
  }, [days, env]);
  useEffect(() => {
    load();
  }, [load]);

  const copy = (link) =>
    navigator.clipboard
      ?.writeText(link)
      .then(() => toast.success(t("Link copied")))
      .catch(() => {});

  const s = data?.summary;
  const items = data ? [...data.items].sort((a, b) => (a.status === "live") === (b.status === "live") ? 0 : a.status === "live" ? -1 : 1) : [];

  return (
    <Page>
      <PageHeader
        title={t("Lives")}
        subtitle={t("The lives started from the app: host, duration, viewers.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />}
      />

      <div className="ledger-stats">
        <div className="ui-card ledger-stat" data-tone={s?.live ? "amber" : undefined}>
          <strong>{s ? fmt(s.live) : "—"}</strong>
          <span>{t("Live now")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{s ? fmt(s.count) : "—"}</strong>
          <span>{t("Lives over the period")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{s ? duration(s.totalMinutes, t) : "—"}</strong>
          <span>{t("Total time live")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{s ? duration(s.averageMinutes, t) : "—"}</strong>
          <span>{t("Average duration")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{s?.bestPeak !== null && s?.bestPeak !== undefined ? fmt(s.bestPeak) : "—"}</strong>
          <span>{t("Best audience")}</span>
        </div>
      </div>

      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <Segmented
            label={t("Period")}
            value={days}
            onChange={setDays}
            options={[
              { value: 7, label: t("7 days") },
              { value: 30, label: t("30 days") },
              { value: 90, label: t("90 days") },
              { value: 365, label: t("1 year") },
            ]}
          />
          <Segmented
            label={t("App")}
            value={env}
            onChange={setEnv}
            options={[
              { value: "prod", label: t("Public app") },
              { value: "dev", label: t("Test app") },
            ]}
          />
        </div>

        {error ? (
          <EmptyState icon="ri-error-warning-line">
            {t(error)}{" "}
            <button type="button" className="ui-btn ui-btn-secondary" onClick={load}>
              {t("Retry")}
            </button>
          </EmptyState>
        ) : !data ? (
          <TableSkeleton />
        ) : !items.length ? (
          <EmptyState icon="ri-broadcast-line">{t("No live over this period")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>{t("Live")}</th>
                  <th>{t("Host")}</th>
                  <th>{t("Started")}</th>
                  <th>{t("Duration")}</th>
                  <th>{t("Viewers")}</th>
                  <th>{t("Participants")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.roomId}>
                    <td>
                      <span className="ui-media-title">{item.title || t("Untitled live")}</span>
                      <span className="ui-media-meta">
                        {item.status === "live" ? <Badge color="red" dot>{t("Live")}</Badge> : <Badge color="slate">{t("Ended")}</Badge>}
                        <code>{item.roomId}</code>
                      </span>
                    </td>
                    <td>{item.hostName || "—"}</td>
                    <td className="ui-cell-muted">{when(item.startedAt)}</td>
                    <td>{duration(item.durationMinutes, t)}</td>
                    <td>
                      {item.status === "live" ? t("{{count}} now", { count: item.viewerCount || 0 }) : "—"}
                      {item.peakViewers !== null && <span className="ui-media-meta">{t("peak {{count}}", { count: item.peakViewers })}</span>}
                    </td>
                    <td>{item.participants !== null ? fmt(item.participants) : "—"}</td>
                    <td>
                      <IconButton icon="ri-links-line" label={t("Copy the link")} onClick={() => copy(item.link)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {s?.hosts?.length > 1 && (
          <p className="ui-hint" style={{ padding: "0 16px 16px" }}>
            {t("Hosts")} : {s.hosts.map((h) => `${h.name} (${h.lives})`).join(" · ")}
          </p>
        )}
      </section>
    </Page>
  );
};

export default Lives;
